import { colors } from '@beelday/common'
import { css, keyframes } from '@emotion/react'
import styled from '@emotion/styled'
import { FC } from 'react'

const sweep = keyframes`
	from {
		background-position: 100% 0;
	}
	to {
		background-position: -100% 0;
	}
`

const breathe = keyframes`
	0%,
	100% {
		opacity: 0.45;
	}
	50% {
		opacity: 1;
	}
`

/**
 * A quiet stand-in for what has not arrived: a light shape with a soft
 * highlight passing over it. Still - a plain shape - where `animated` is off
 * (the thing is not coming) and for anyone who asked for less motion.
 * Decoration only: whoever uses it says in words what is going on.
 */
export const SkeletonBlock = styled.div<{ animated?: boolean }>`
	border-radius: 8px;
	background-color: ${colors.indigoBlue10};

	${({ animated = true }) =>
		animated &&
		css`
			background-image: linear-gradient(
				90deg,
				${colors.indigoBlue10} 30%,
				${colors.indigoBlue05} 50%,
				${colors.indigoBlue10} 70%
			);
			background-size: 200% 100%;
			animation: ${sweep} 1.8s ease-in-out infinite;
		`}

	@media (prefers-reduced-motion: reduce) {
		animation: none;
	}
`

/** The heights, in percent, of the bars of a recording that has no shape yet. */
const WAVE = [
	30, 45, 38, 62, 80, 55, 70, 90, 66, 48, 58, 76, 100, 84, 60, 72, 50, 40, 64,
	86, 94, 70, 52, 44, 68, 82, 58, 36, 54, 74, 88, 62, 46, 34, 56, 78, 66, 42,
	50, 60, 40, 32, 48, 38,
]

const Wave = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 4px;
	width: 100%;
	height: 100%;
`

const Bar = styled.span`
	flex: 1;
	min-width: 0;
	border-radius: 2px;
	background-color: ${colors.indigoBlue10};
	animation: ${breathe} 2.4s ease-in-out infinite;

	@media (prefers-reduced-motion: reduce) {
		animation: none;
		opacity: 0.7;
	}
`

/**
 * The waveform of a recording that is on its way: bars of an arbitrary
 * shape that rise and fall in turn, calmly. It stands still for anyone who
 * asked for less motion.
 */
export const SkeletonWave: FC = () => (
	<Wave aria-hidden="true" data-testid="skeleton.wave">
		{WAVE.map((height, index) => (
			<Bar
				key={index}
				style={{ height: `${height}%`, animationDelay: `${index * 60}ms` }}
			/>
		))}
	</Wave>
)
